import { useEffect, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import {
  X,
  Bell,
  Smartphone,
  Send,
  Link2,
  ImageIcon,
  Type,
} from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Badge } from '../ui/badge';

export interface PushNotificationFormValues {
  title: string;
  body: string;
  route?: string;
  ctaLabel?: string;
  imageUrl?: string;
  previewText?: string;
}

interface CreatePushNotificationModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (values: PushNotificationFormValues) => Promise<void>;
  loading?: boolean;
  initialValues?: Partial<PushNotificationFormValues>;
  mode?: "create" | "resend";
}

export function CreatePushNotificationModal({
  isOpen,
  onClose,
  onSubmit,
  loading = false,
  initialValues,
  mode = "create",
}: CreatePushNotificationModalProps) {
  const [title, setTitle] = useState(initialValues?.title ?? '');
  const [body, setBody] = useState(initialValues?.body ?? '');
  const [ctaLabel, setCtaLabel] = useState(initialValues?.ctaLabel ?? "View Updates");
  const [route, setRoute] = useState(initialValues?.route ?? '');
  const [imageUrl, setImageUrl] = useState(initialValues?.imageUrl ?? '');
  const [previewText, setPreviewText] = useState(initialValues?.previewText ?? '');
  const [showPreview, setShowPreview] = useState(true);
  const isResend = mode === "resend" || Boolean(initialValues);

  useEffect(() => {
    if (isOpen) {
      setTitle(initialValues?.title ?? '');
      setBody(initialValues?.body ?? '');
      setRoute(initialValues?.route ?? '');
      setImageUrl(initialValues?.imageUrl ?? '');
      setPreviewText(initialValues?.previewText ?? '');
      setCtaLabel(initialValues?.ctaLabel ?? "View Updates");
      setShowPreview(true);
    }
  }, [isOpen, initialValues]);

  const handleSubmit = async () => {
    if (!title.trim() || !body.trim()) {
      return;
    }

    await onSubmit({
      title: title.trim(),
      body: body.trim(),
      route: route.trim() ? route.trim() : undefined,
      ctaLabel: ctaLabel.trim() ? ctaLabel.trim() : undefined,
      imageUrl: imageUrl.trim() ? imageUrl.trim() : undefined,
      previewText: previewText.trim() ? previewText.trim() : undefined,
    });
  };

  const renderPreview = () => (
    <div className="bg-gradient-to-b from-gray-900 to-black rounded-3xl p-6 text-white shadow-2xl border border-white/10">
      <div className="flex items-center justify-between text-xs text-white/70 mb-6">
        <span>Now</span>
        <div className="flex items-center gap-2">
          <span>JamPack'd</span>
          <Bell className="w-4 h-4" />
        </div>
      </div>

      <div className="bg-white/10 rounded-2xl p-4 space-y-3 backdrop-blur">
        <div className="flex items-center gap-2 text-xs text-white/80">
          <img
            src="/logo-square.png"
            alt="JamPack'd"
            className="w-6 h-6 rounded-md border border-white/20"
            onError={(e) => {
              (e.target as HTMLImageElement).style.display = 'none';
            }}
          />
          <span>JamPack'd Mobile</span>
        </div>
        <div>
          <p className="font-semibold text-base">{title || 'Your notification title'}</p>
          <p className="text-sm text-white/80 mt-1">
            {body || "Craft a concise message that gets users excited to open the app."}
          </p>
        </div>
        {imageUrl && (
          <div className="rounded-xl overflow-hidden border border-white/20">
            <img
              src={imageUrl}
              alt="Notification preview"
              className="w-full h-32 object-cover"
            />
          </div>
        )}
        <Button
          variant="secondary"
          className="w-full bg-white text-gray-900 hover:bg-white/90"
        >
          {ctaLabel || 'Open JamPack’d'}
        </Button>
      </div>

      <p className="text-center text-xs text-white/60 mt-4">
        Preview only. Actual rendering follows iOS/Android notification styles.
      </p>
    </div>
  );

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-[10001] bg-black/40 backdrop-blur-sm flex items-center justify-center p-4"
          onClick={(e) => e.target === e.currentTarget && onClose()}
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.95, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: 20 }}
            transition={{ type: 'spring', duration: 0.3 }}
            className="bg-white rounded-3xl shadow-2xl w-full max-w-5xl max-h-[90vh] overflow-hidden flex flex-col"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="bg-gradient-to-r from-jamaica-green to-jamaica-green/80 px-8 py-6 flex items-center justify-between shrink-0">
              <div className="flex items-center gap-4">
                <div className="h-12 w-12 bg-white/20 rounded-2xl flex items-center justify-center border border-white/30">
                  <Smartphone className="w-6 h-6 text-white" />
                </div>
                <div>
                  <h2 className="text-2xl font-bold text-white">
                    {isResend ? 'Resend Broadcast Push Notification' : 'Create Broadcast Push Notification'}
                  </h2>
                  <p className="text-white/80 text-sm">
                    {isResend
                      ? 'Review or tweak the previous announcement before sending it again.'
                      : "Reach every JamPack'd mobile user with a curated announcement"}
                  </p>
                </div>
              </div>
              <Button
                variant="ghost"
                size="sm"
                onClick={onClose}
                className="text-white hover:bg-white/20"
              >
                <X className="w-4 h-4" />
              </Button>
            </div>

            <div className="flex-1">
              <div className="grid grid-cols-1 lg:grid-cols-2 h-full">
                {/* Form */}
                <div className="border-r border-gray-100">
                  <div className="max-h-[calc(90vh-150px)] overflow-y-auto">
                    <div className="p-8 space-y-6">
                      <div>
                        <Label className="text-sm font-semibold text-gray-700">Title</Label>
                        <Input
                          value={title}
                          onChange={(e) => setTitle(e.target.value)}
                          placeholder="Fresh drop now live"
                          className="mt-2"
                        />
                      </div>

                      <div>
                        <Label className="text-sm font-semibold text-gray-700">Body</Label>
                        <Textarea
                          value={body}
                          onChange={(e) => setBody(e.target.value)}
                          placeholder="Tap to explore the all-new JamRock Holiday Box before it sells out."
                          className="mt-2 min-h-[110px]"
                        />
                      </div>

                      <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                        <div>
                          <Label className="text-sm font-semibold text-gray-700 flex items-center gap-2">
                            <Link2 className="w-4 h-4 text-gray-400" />
                            Deep Link Route (optional)
                          </Label>
                          <Input
                            value={route}
                            onChange={(e) => setRoute(e.target.value)}
                            placeholder="/collections/holiday"
                            className="mt-2"
                          />
                          <p className="text-xs text-gray-500 mt-1">
                            Relative path inside the customer storefront.
                          </p>
                        </div>
                        <div>
                          <Label className="text-sm font-semibold text-gray-700 flex items-center gap-2">
                            <Type className="w-4 h-4 text-gray-400" />
                            CTA Label
                          </Label>
                          <Input
                            value={ctaLabel}
                            onChange={(e) => setCtaLabel(e.target.value)}
                            placeholder="Shop now"
                            className="mt-2"
                          />
                        </div>
                      </div>

                      <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                        <div>
                          <Label className="text-sm font-semibold text-gray-700 flex items-center gap-2">
                            <ImageIcon className="w-4 h-4 text-gray-400" />
                            Hero Image (optional)
                          </Label>
                          <Input
                            value={imageUrl}
                            onChange={(e) => setImageUrl(e.target.value)}
                            placeholder="https://..."
                            className="mt-2"
                          />
                        </div>
                        <div>
                          <Label className="text-sm font-semibold text-gray-700">Preview Summary</Label>
                          <Input
                            value={previewText}
                            onChange={(e) => setPreviewText(e.target.value)}
                            placeholder="Holiday Box drop • Limited qty"
                            className="mt-2"
                          />
                        </div>
                      </div>

                      <div className="bg-gray-50 rounded-2xl p-4 border border-gray-100 flex items-start gap-3">
                        <Badge className="bg-jamaica-green/10 text-jamaica-green">
                          Audience
                        </Badge>
                        <div>
                          <p className="font-medium text-gray-900">Broadcast to all mobile installs</p>
                          <p className="text-sm text-gray-600">
                            The topic <code className="bg-gray-200 px-1 rounded text-xs">all</code> automatically
                            targets every Android and iOS device with the JamPack’d app.
                          </p>
                        </div>
                      </div>

                      <div className="flex items-center justify-end gap-3">
                        <Button variant="outline" onClick={() => setShowPreview((prev) => !prev)}>
                          {showPreview ? 'Hide Preview' : 'Show Preview'}
                        </Button>
                        <Button
                          onClick={handleSubmit}
                          disabled={!title.trim() || !body.trim() || loading}
                          className="bg-jamaica-green hover:bg-jamaica-green/90 text-white flex items-center gap-2"
                        >
                          {loading ? (
                            <span className="flex items-center gap-2">
                              <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
                                <circle
                                  className="opacity-25"
                                  cx="12"
                                  cy="12"
                                  r="10"
                                  stroke="currentColor"
                                  strokeWidth="4"
                                ></circle>
                                <path
                                  className="opacity-75"
                                  fill="currentColor"
                                  d="M4 12a8 8 0 018-8v4l3-3-3-3v4a8 8 0 00-8 8h4z"
                                ></path>
                              </svg>
                              {isResend ? 'Resending…' : 'Sending…'}
                            </span>
                          ) : (
                            <>
                              <Send className="w-4 h-4" />
                              {isResend ? 'Resend Broadcast' : 'Send Broadcast'}
                            </>
                          )}
                        </Button>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Preview */}
                <div className="bg-gray-900/95 p-8 flex items-center justify-center">
                  <div className="w-full flex items-center justify-center">
                    <AnimatePresence>
                      {showPreview && (
                        <motion.div
                          initial={{ opacity: 0, y: 20 }}
                          animate={{ opacity: 1, y: 0 }}
                          exit={{ opacity: 0, y: 20 }}
                          transition={{ duration: 0.3 }}
                          className="w-full max-w-xs"
                        >
                          {renderPreview()}
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
